import { Skeleton } from "@mui/material";
import { IconBookmark, IconBookmarkFilled } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import grainImg from "../../assets/images/collection-cover-grain.svg";
import { collectionBackdrop } from "../../domains/playlist/collectionBackdrop";

type DesktopCollectionCardPlaylist = {
  id: string;
  title: string;
  images: { url: string; sizes: { medium: { file: string } } | null }[];
  metrics: { articles: number; journals: number };
  featuredArticle: { title: string; journalName: string | null } | null;
};

/** The sizes of Figma ".CollectionCover": L on desktop, M on mobile. */
const COVER_SIZES = {
  L: {
    cover: "h-46",
    backdrop: "-inset-x-10 -top-15 -bottom-14",
    backPaper: "-right-9 -bottom-5.5 h-32 w-56",
    paper: "top-22 -right-12.5 w-58.5 gap-1 p-3.5",
    journal: "text-sm text-ds-mint-800",
    article: "text-ds-black",
  },
  M: {
    cover: "h-32",
    backdrop: "-inset-x-5 -top-8 -bottom-8",
    backPaper: "-right-2 -bottom-5 h-26 w-57.5",
    paper: "top-9.5 -right-7 h-30 w-60 gap-0.5 px-2.5 py-2",
    journal: "text-caption text-ds-mint-800/50",
    article: "text-ds-black/50",
  },
} as const;

/**
 * The cover of a collection: its image, or its gradient when it has no image,
 * under the paper of its first article. The image stays sharp: a blur leaves only
 * its colours. A collection with no article yet has no paper. At the M size the
 * paper's text stays in the background: the collection's title below is what the
 * reader reads.
 */
function CollectionCover({
  playlist,
  size,
}: {
  playlist: DesktopCollectionCardPlaylist;
  size: keyof typeof COVER_SIZES;
}): React.JSX.Element {
  const classes = COVER_SIZES[size];
  const image = playlist.images.at(0);
  const { featuredArticle } = playlist;
  return (
    <div
      className={`relative ${classes.cover} w-full shrink-0 overflow-hidden rounded-xl bg-ds-slate-100`}
    >
      {/* The blurred gradient is wider than the cover, so the blur does not fade
          at the edges. The bottom of the image shows, as the design crops it. The
          filters are the design's image adjustments (exposure, contrast,
          saturation). */}
      <div
        aria-hidden
        className={`absolute ${image === undefined ? `${classes.backdrop} blur-collection-cover` : "inset-0"} bg-cover bg-bottom brightness-110 contrast-85 saturate-120`}
        style={{
          backgroundImage:
            image === undefined
              ? collectionBackdrop(playlist.id)
              : `url(${
                  image.sizes === null ? image.url : image.sizes.medium.file
                })`,
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-8"
        style={{ backgroundImage: `url(${grainImg})` }}
      />
      {featuredArticle === null ? null : (
        <>
          <div
            className={`absolute ${classes.backPaper} -rotate-4 rounded-lg bg-ds-white/70`}
          />
          <div
            className={`absolute ${classes.paper} flex rotate-2 flex-col rounded-lg bg-ds-white shadow-collection-paper transition duration-300 group-hover:-translate-y-0.5 group-hover:rotate-3 group-hover:shadow-collection-paper-hover`}
          >
            {featuredArticle.journalName === null ? null : (
              <p className={`truncate ${classes.journal} font-medium`}>
                {featuredArticle.journalName}
              </p>
            )}
            <p
              className={`line-clamp-3 text-desktop-meta font-semibold ${classes.article}`}
            >
              {featuredArticle.title}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * A collection on desktop (Figma "CollectionCard"): its cover, the title and
 * the counts, in the grids and bands of the desktop pages. The cover is the
 * collection's image under the paper of its first article (Figma
 * ".CollectionCover", size L); a collection with no article yet has no paper.
 *
 * The whole card opens the collection. The bookmark, when the reader can save
 * the collection, is a separate button above it.
 */
export function DesktopCollectionCard({
  playlist,
  onOpen,
  bookmark,
}: {
  playlist: DesktopCollectionCardPlaylist;
  onOpen: () => void;
  bookmark: { isSaved: boolean; onToggle: () => void } | null;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="group relative flex flex-col gap-2.5 font-dm-sans">
      {/* The card's own target, laid over it so the bookmark can sit inside.
          First, so Tab reaches the card before its bookmark. */}
      <button
        type="button"
        aria-label={playlist.title}
        onClick={onOpen}
        className="absolute inset-0 z-1 cursor-pointer rounded-xl border-none bg-transparent p-0 focus-visible:outline-3 focus-visible:outline-offset-3 focus-visible:outline-focus-ring"
      />
      <CollectionCover playlist={playlist} size="L" />
      <p className="line-clamp-3 text-desktop-card-title font-semibold text-ds-black">
        {playlist.title}
      </p>
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 truncate text-desktop-meta text-ds-slate-600">
          {playlist.metrics.journals === 0
            ? t("{{count}} articles", { count: playlist.metrics.articles })
            : `${t("{{count}} articles", { count: playlist.metrics.articles })} · ${t("{{count}} journals", { count: playlist.metrics.journals })}`}
        </p>
        {bookmark === null ? null : (
          <button
            type="button"
            aria-label={bookmark.isSaved ? t("Saved") : t("Save")}
            aria-pressed={bookmark.isSaved}
            onClick={bookmark.onToggle}
            className="relative z-2 flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-sm border-none bg-transparent p-0 text-ds-slate-700 hover:text-ds-black focus-visible:shadow-focus-ring focus-visible:outline-none"
          >
            {bookmark.isSaved ? (
              <IconBookmarkFilled className="size-5" />
            ) : (
              <IconBookmark className="size-5" stroke={1.5} />
            )}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * A collection on mobile (Figma "CollectionCard · mobile"): the same card at the
 * M cover, at 75% of the row of a collections band. The whole card opens the
 * collection, and it has no bookmark, as the design draws it.
 */
export function MobileCollectionCard({
  playlist,
  onOpen,
}: {
  playlist: DesktopCollectionCardPlaylist;
  onOpen: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      aria-label={playlist.title}
      onClick={onOpen}
      className="flex w-full cursor-pointer flex-col gap-2 border-none bg-transparent p-0 text-left font-dm-sans"
    >
      <CollectionCover playlist={playlist} size="M" />
      <p className="line-clamp-3 text-desktop-card-title font-semibold text-ds-black">
        {playlist.title}
      </p>
      <p className="truncate text-desktop-meta text-ds-slate-600">
        {playlist.metrics.journals === 0
          ? t("{{count}} articles", { count: playlist.metrics.articles })
          : `${t("{{count}} articles", { count: playlist.metrics.articles })} · ${t("{{count}} journals", { count: playlist.metrics.journals })}`}
      </p>
    </button>
  );
}

export function MobileCollectionCardSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <Skeleton
        variant="rectangular"
        className="h-32 w-full shrink-0 rounded-xl"
        sx={{ bgcolor: "rgba(0, 0, 0, 0.08)" }}
      />
      <Skeleton variant="text" width="90%" />
      <Skeleton variant="text" width="40%" />
    </div>
  );
}

export function DesktopCollectionCardSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2.5">
      <Skeleton
        variant="rectangular"
        className="h-46 w-full shrink-0 rounded-xl"
        sx={{ bgcolor: "rgba(0, 0, 0, 0.08)" }}
      />
      <Skeleton variant="text" width="90%" />
      <Skeleton variant="text" width="40%" />
    </div>
  );
}
